import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { Chart } from 'react-chartjs-2';
import {
  Typography,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
} from '@material-ui/core';

import { PaperTitle, PaperTitleText, PaperContent } from '../../primitive/Paper';
import {
  classifyMetric,
  LinkPrefetchReportData,
  LinkPrefetchReportThreshold,
  MetricCategoryLabel,
} from '../../../../hooks/useLinkPrefetchReport';
import SpeculationRule from '../../../../entity/SpeculationRule';
import HelpIcon from '@material-ui/icons/Help';

interface MetricData {
  label: string;
  before: string;
  after: string;
  improvement: number;
}

interface AggregatedPatternData {
  pv: number;
  control: {
    pv: number;
    dcl: number;
    lcp: number;
    cls: number;
  };
  experimental: {
    pv: number;
    dcl: number;
    lcp: number;
    cls: number;
  };
  dailies: Array<{
    date: string;
    control: {
      pv: number;
      dcl: number;
      lcp: number;
      cls: number;
    };
    experimental: {
      pv: number;
      dcl: number;
      lcp: number;
      cls: number;
    };
  }>;
}

type Props = {
  reportData: LinkPrefetchReportData;
  reportThreshold: LinkPrefetchReportThreshold;
  speculationRule: SpeculationRule;
};

const SITE_WIDE_LABEL = 'サイト全体平均';

const METRIC_CATEGORY_HELP_URL = 'https://support.kaizenplatform.net/hc/ja/articles/56505141589529';

function renderCategoryChip(
  controlRaw: number,
  experimentalRaw: number,
  warnThreshold: number,
  poorThreshold: number,
): JSX.Element {
  const controlCategory = classifyMetric(controlRaw, warnThreshold, poorThreshold);
  const experimentalCategory = classifyMetric(experimentalRaw, warnThreshold, poorThreshold);
  let ChipComponent: typeof ImprovementChipBetter;
  let label: string;
  if (controlCategory === experimentalCategory) {
    ChipComponent =
      controlCategory === 0
        ? ImprovementChipBetter
        : controlCategory === 1
        ? ImprovementChipNeutral
        : ImprovementChipWorse;
    label = `「${MetricCategoryLabel[controlCategory]}」範囲内`;
  } else {
    label = `「${MetricCategoryLabel[controlCategory]}」→「${MetricCategoryLabel[experimentalCategory]}」`;
    ChipComponent = experimentalCategory < controlCategory ? ImprovementChipBetter : ImprovementChipWorse;
  }
  return (
    <ChipWithHelp>
      <ChipComponent label={label} />
      <HelpLink href={METRIC_CATEGORY_HELP_URL} target="_blank" rel="noopener noreferrer">
        ?
      </HelpLink>
    </ChipWithHelp>
  );
}

const ReportChart: FC<Props> = ({ reportData, reportThreshold, speculationRule }) => {
  const [targetSelect, setTargetSelect] = useState(SITE_WIDE_LABEL);
  const [activeMetric, setActiveMetric] = useState('DCL');
  const pvThreshold = reportThreshold.pv;

  // グループ化されたデータを構築（同名グループを区別するためキーにインデックスを使用）
  type GroupEntry = { key: string; name: string; data: AggregatedPatternData };
  const groupEntries: GroupEntry[] = [];
  const grouping = speculationRule.whitelistPathGrouping || [];

  // サイト全体平均を追加
  if (reportData.patterns['']) {
    groupEntries.push({ key: SITE_WIDE_LABEL, name: SITE_WIDE_LABEL, data: reportData.patterns[''] });
  }

  // 各グループについて集計
  grouping.forEach(([groupName, patterns], groupIndex) => {
    const allDates = new Set<string>();
    const dailyData: { [date: string]: { control: any; experimental: any } } = {};

    let totalControlPv = 0;
    let totalExperimentalPv = 0;
    let totalControlDcl = 0;
    let totalControlLcp = 0;
    let totalControlCls = 0;
    let totalExperimentalDcl = 0;
    let totalExperimentalLcp = 0;
    let totalExperimentalCls = 0;

    // グループ内の各パターンを集計
    patterns.forEach((pattern) => {
      if (reportData.patterns[pattern]) {
        const data = reportData.patterns[pattern];

        // 日次データを集計
        data.dailies.forEach((daily) => {
          allDates.add(daily.date);
          if (!dailyData[daily.date]) {
            dailyData[daily.date] = {
              control: { pv: 0, dcl: 0, lcp: 0, cls: 0 },
              experimental: { pv: 0, dcl: 0, lcp: 0, cls: 0 },
            };
          }

          const d = dailyData[daily.date];
          d.control.pv += daily.control.pv;
          d.control.dcl += daily.control.dcl * daily.control.pv;
          d.control.lcp += daily.control.lcp * daily.control.pv;
          d.control.cls += daily.control.cls * daily.control.pv;
          d.experimental.pv += daily.experimental.pv;
          d.experimental.dcl += daily.experimental.dcl * daily.experimental.pv;
          d.experimental.lcp += daily.experimental.lcp * daily.experimental.pv;
          d.experimental.cls += daily.experimental.cls * daily.experimental.pv;
        });

        // 全体集計
        totalControlPv += data.control.pv;
        totalExperimentalPv += data.experimental.pv;
        totalControlDcl += data.control.dcl * data.control.pv;
        totalControlLcp += data.control.lcp * data.control.pv;
        totalControlCls += data.control.cls * data.control.pv;
        totalExperimentalDcl += data.experimental.dcl * data.experimental.pv;
        totalExperimentalLcp += data.experimental.lcp * data.experimental.pv;
        totalExperimentalCls += data.experimental.cls * data.experimental.pv;
      }
    });

    if (allDates.size > 0) {
      // 日次データの平均値を計算
      const dailies = Array.from(allDates)
        .sort()
        .map((date) => {
          const d = dailyData[date];
          return {
            date,
            control: {
              pv: d.control.pv,
              dcl: d.control.pv > 0 ? d.control.dcl / d.control.pv : 0,
              lcp: d.control.pv > 0 ? d.control.lcp / d.control.pv : 0,
              cls: d.control.pv > 0 ? d.control.cls / d.control.pv : 0,
            },
            experimental: {
              pv: d.experimental.pv,
              dcl: d.experimental.pv > 0 ? d.experimental.dcl / d.experimental.pv : 0,
              lcp: d.experimental.pv > 0 ? d.experimental.lcp / d.experimental.pv : 0,
              cls: d.experimental.pv > 0 ? d.experimental.cls / d.experimental.pv : 0,
            },
          };
        });

      groupEntries.push({
        key: `group_${groupIndex}`,
        name: groupName,
        data: {
          pv: totalControlPv + totalExperimentalPv,
          control: {
            pv: totalControlPv,
            dcl: totalControlPv > 0 ? totalControlDcl / totalControlPv : 0,
            lcp: totalControlPv > 0 ? totalControlLcp / totalControlPv : 0,
            cls: totalControlPv > 0 ? totalControlCls / totalControlPv : 0,
          },
          experimental: {
            pv: totalExperimentalPv,
            dcl: totalExperimentalPv > 0 ? totalExperimentalDcl / totalExperimentalPv : 0,
            lcp: totalExperimentalPv > 0 ? totalExperimentalLcp / totalExperimentalPv : 0,
            cls: totalExperimentalPv > 0 ? totalExperimentalCls / totalExperimentalPv : 0,
          },
          dailies,
        },
      });
    }
  });

  // サイト全体以外のグループをPV降順で取得
  const groupSelectEntries = groupEntries
    .filter((entry) => entry.key !== SITE_WIDE_LABEL)
    .sort((a, b) => b.data.experimental.pv - a.data.experimental.pv);

  const selectedEntry = groupEntries.find((entry) => entry.key === targetSelect);
  const pattern = selectedEntry?.data;
  if (!pattern) {
    if (targetSelect !== SITE_WIDE_LABEL) {
      setTargetSelect(SITE_WIDE_LABEL);
    }
    return <div>データが存在しません</div>;
  }

  // レスポンスデータからチャートデータを生成
  const generateChartData = () => {
    // ラベルを生成（MM/DD形式）
    const labels = pattern.dailies.map((daily) => {
      const [, month, day] = daily.date.split('-');
      return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
    });

    const datasets = [];
    if (activeMetric === 'DCL' || activeMetric === '両方表示') {
      const dclTestData = pattern.dailies.map((daily) => daily.experimental.dcl / 1000);
      const dclControlData = pattern.dailies.map((daily) => daily.control.dcl / 1000);
      datasets.push(
        {
          label: 'DCL 設定グループ (90%)',
          data: dclTestData,
          borderColor: '#6366f1',
          pointRadius: 4,
          pointBackgroundColor: '#6366f1',
          backgroundColor: 'transparent',
        },
        {
          label: 'DCL 未設定グループ (10%)',
          data: dclControlData,
          borderColor: '#9ca3af',
          pointRadius: 4,
          pointBackgroundColor: '#9ca3af',
          backgroundColor: 'transparent',
          borderDash: [6, 6],
        },
      );
    }
    if (activeMetric === 'LCP' || activeMetric === '両方表示') {
      const lcpTestData = pattern.dailies.map((daily) => daily.experimental.lcp / 1000);
      const lcpControlData = pattern.dailies.map((daily) => daily.control.lcp / 1000);
      datasets.push(
        {
          label: 'LCP 設定グループ (90%)',
          data: lcpTestData,
          borderColor: '#a855f7',
          pointRadius: 4,
          pointBackgroundColor: '#a855f7',
          backgroundColor: 'transparent',
        },
        {
          label: 'LCP 未設定グループ (10%)',
          data: lcpControlData,
          borderColor: '#c4b5fd',
          pointRadius: 4,
          pointBackgroundColor: '#c4b5fd',
          backgroundColor: 'transparent',
          borderDash: [6, 6],
        },
      );
    }

    return {
      labels,
      datasets,
    };
  };
  const chartData = generateChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
      },
    },
    scales: {
      y: {
        min: 0.0,
        ticks: {
          callback: function (value: any) {
            return value.toFixed(2) + '秒';
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const metricsData: MetricData[] = (() => {
    const calculateImprovement = (before: number, after: number) => {
      if (before === 0) {
        return 0;
      }
      // DCL, LCP, CLS は値が小さいほど良いため、 (before - after) で改善度を計算
      return ((before - after) / before) * 100;
    };

    const dclImprovement = calculateImprovement(pattern.control.dcl, pattern.experimental.dcl);
    const lcpImprovement = calculateImprovement(pattern.control.lcp, pattern.experimental.lcp);
    const clsImprovement = calculateImprovement(pattern.control.cls, pattern.experimental.cls);

    return [
      {
        label: 'DOM Content Loaded (DCL)',
        before: `${(pattern.control.dcl / 1000).toFixed(2)}秒`,
        after: `${(pattern.experimental.dcl / 1000).toFixed(2)}秒`,
        improvement: Math.round(dclImprovement),
      },
      {
        label: 'Largest Contentful Paint (LCP)',
        before: `${(pattern.control.lcp / 1000).toFixed(2)}秒`,
        after: `${(pattern.experimental.lcp / 1000).toFixed(2)}秒`,
        improvement: Math.round(lcpImprovement),
      },
      {
        label: 'Cumulative Layout Shift (CLS)',
        before: (pattern.control.cls / 1000).toFixed(2),
        after: (pattern.experimental.cls / 1000).toFixed(2),
        improvement: Math.round(clsImprovement),
      },
    ];
  })();

  return (
    <Container>
      <PaperTitle>
        <TitleWithButtons>
          <PaperTitleText>推移グラフ</PaperTitleText>
          <RightSection>
            <FormControl size="small" style={{ minWidth: 150, marginRight: 16 }}>
              <InputLabel>表示対象</InputLabel>
              <Select value={targetSelect} onChange={(e) => setTargetSelect(e.target.value as string)}>
                <MenuItem value={SITE_WIDE_LABEL}>{SITE_WIDE_LABEL}</MenuItem>
                {groupSelectEntries.map((entry) => (
                  <MenuItem key={entry.key} value={entry.key}>
                    {entry.name.length > 30 ? entry.name.slice(0, 30) + '...' : entry.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MetricButtons>
              {['DCL', 'LCP', '両方表示'].map((metric) => (
                <MetricButton
                  key={metric}
                  variant={activeMetric === metric ? 'outlined' : 'contained'}
                  size="small"
                  onClick={() => setActiveMetric(metric)}
                >
                  {metric}
                </MetricButton>
              ))}
            </MetricButtons>
          </RightSection>
        </TitleWithButtons>
      </PaperTitle>
      <PaperContent>
        <ChartTopLeftNote>
          <Typography variant="body2" color="textSecondary">
            {selectedEntry?.name ?? targetSelect}の読み込み時間
          </Typography>
        </ChartTopLeftNote>
        <ChartWrapper>
          <Chart type="line" data={chartData} options={chartOptions} />
        </ChartWrapper>
      </PaperContent>

      <MetricsContainer>
        {metricsData.map((metric, index) => (
          <MetricCard key={index}>
            <CardContent>
              <MetricLabel variant="body2">{metric.label}</MetricLabel>
              <>
                <MetricValues>
                  <MetricValue variant="h6">{metric.before}</MetricValue>
                  <ArrowIcon>→</ArrowIcon>
                  <MetricValue variant="h6">{metric.after}</MetricValue>
                </MetricValues>
                {pattern.control.pv === 0 || pattern.experimental.pv < pvThreshold ? (
                  <>
                    <Tooltip title="有効PVが不足しています">
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ImprovementChipInsufficient label="データ不足" />
                        <HelpIcon style={{ fontSize: 16 }} />
                      </div>
                    </Tooltip>
                  </>
                ) : index === 1 ? (
                  renderCategoryChip(
                    pattern.control.lcp,
                    pattern.experimental.lcp,
                    reportThreshold.lcpWarn,
                    reportThreshold.lcpPoor,
                  )
                ) : index === 2 ? (
                  renderCategoryChip(
                    pattern.control.cls,
                    pattern.experimental.cls,
                    reportThreshold.clsWarn,
                    reportThreshold.clsPoor,
                  )
                ) : metric.improvement > 0 ? (
                  <ImprovementChipBetter label={`${metric.improvement}%改善`} />
                ) : metric.improvement < 0 ? (
                  <ImprovementChipWorse label={`${-metric.improvement}%悪化`} />
                ) : (
                  <ImprovementChipNeutral label={`${metric.improvement}%改善`} />
                )}
              </>
            </CardContent>
          </MetricCard>
        ))}
        <MetricsNote>
          同指標（「良好」等）範囲内であれば、数値上悪化していても、ユーザー体験にもSEO評価にも影響はございません。
          <br />
          ※元々評価が低い場合には、本機能以外の方法での改善が推奨されます。
        </MetricsNote>
      </MetricsContainer>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

const TitleWithButtons = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const RightSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const MetricButtons = styled('div')({
  display: 'flex',
});

const MetricButton = styled(Button)<{ variant: 'contained' | 'outlined' }>(({ variant }) => ({
  minWidth: '60px',
  fontSize: '12px',
  ...(variant === 'outlined' && {
    backgroundColor: '#eeeeee',
  }),
}));

const ChartTopLeftNote = styled('div')({
  width: '100%',
  marginTop: 8,
});

const ChartWrapper = styled('div')({
  height: '420px',
  marginTop: '8px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  padding: '0 24px 24px 24px',
  borderRadius: '4px',
});

const MetricsContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  maxWidth: '1280px',
  margin: '24px auto 0 auto',
  padding: '24px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
});

const MetricCard = styled(Card)({
  textAlign: 'center',
});

const MetricLabel = styled(Typography)({
  marginBottom: '8px',
  fontWeight: 500,
});

const MetricValues = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginBottom: '8px',
});

const MetricValue = styled(Typography)({
  fontWeight: 600,
});

const ArrowIcon = styled('span')({
  color: '#666',
  fontSize: '18px',
});

const ImprovementChipBetter = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#E8F5E8',
  color: '#2E7D32',
});

const ImprovementChipWorse = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#FDECEA',
  color: '#C62828',
});

const ImprovementChipNeutral = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#FFF8E1',
  color: '#FF8F00',
});

const ImprovementChipInsufficient = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#F5F5F5',
  color: '#9E9E9E',
});

const MetricsNote = styled('div')({
  gridColumn: '1 / -1',
  paddingLeft: 'calc((100% - 32px) / 3 + 16px)',
  fontSize: '12px',
  color: '#666',
  lineHeight: 1.6,
});

const ChipWithHelp = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
});

const HelpLink = styled('a')({
  fontSize: '11px',
  fontWeight: 600,
  color: '#1976d2',
  border: '1px solid #1976d2',
  borderRadius: '50%',
  width: '16px',
  height: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: '#e3f2fd',
  },
});

export default ReportChart;
